// src/finance/components/MonthEditor.tsx
// Édition d'un mois réel : sauvegarde explicite (bouton), deltas vs objectif.
import React, { useState } from 'react'
import type { FinanceMonth, FinanceSettings } from '../../lib/types'
import { estimateNetForMonth } from '../engine/engine'
import type { EffectiveTargets } from '../targets'
import { fmtEur, parseAmount } from '../format'
import { ExpenseListEditor, type EditableExpenseLine } from './ExpenseListEditor'
import { InfoTip } from './InfoTip'
import type { MonthExpenseInput } from '../api'

export type MonthDraft = {
  revenue: number
  salaryPaid: number
  dividendsPaid: number
  note: string
  expenses: MonthExpenseInput[]
}

const numberField: React.CSSProperties = {
  background: 'var(--bg)',
  border: '1px solid var(--line)',
  borderRadius: '6px',
  color: 'var(--fg)',
  fontSize: '0.875rem',
  fontFamily: 'var(--mono)',
  padding: '0.5rem 0.625rem',
  width: '120px',
}

/** Cumul annuel des versements des mois CHRONOLOGIQUEMENT antérieurs à `month`.
 *
 *  L'IR est progressif et se liquide par ANNÉE CIVILE : le net d'un mois ne se
 *  déduit pas du mois isolé, mais du différentiel d'IR entre le cumul « avec ce
 *  mois » et le cumul « avant ce mois » (cf. `estimateNetForMonth`). D'où deux
 *  filtres : même année (`YYYY-`) et rang chronologique strictement inférieur —
 *  les mois postérieurs déjà saisis ne doivent PAS entrer dans le cumul. */
export function cumulBeforeMonth(
  months: { month: string; salaryPaid: number; dividendsPaid: number }[],
  month: string,
): { salary: number; dividends: number } {
  const year = month.slice(0, 5)   // 'YYYY-'
  return months
    .filter((m) => m.month.startsWith(year) && m.month < month)
    .reduce(
      (acc, m) => ({ salary: acc.salary + m.salaryPaid, dividends: acc.dividends + m.dividendsPaid }),
      { salary: 0, dividends: 0 },
    )
}

function Delta({
  label,
  actual,
  target,
  invert,
  unavailable,
  neutral,
  testId,
}: {
  label: string
  actual: number
  target: number
  /** true quand dépasser l'objectif est MAUVAIS (dépenses). */
  invert?: boolean | undefined
  /**
   * true quand il n'y a PAS d'objectif à opposer (cible inatteignable, C5) :
   * `targets` vaut alors 0 partout, et un delta contre 0 se lisait « +5 000 € vs
   * objectif » en bronze — un triomphe complet sous une bannière annonçant une
   * cible impossible.
   */
  unavailable?: boolean | undefined
  /**
   * true quand le delta n'est pas un verdict : deux grandeurs comparables en
   * cumul mais pas mois par mois (cf. la note sous le net perso estimé). On
   * affiche l'écart sans le peindre en « atteint » / « manqué ».
   */
  neutral?: boolean | undefined
  testId?: string | undefined
}) {
  const delta = actual - target
  const isGood = invert ? delta <= 0 : delta >= 0
  const color = neutral ? 'var(--muted)' : isGood ? 'var(--bronze)' : 'var(--arancio)'
  return (
    <span data-testid={testId} style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
      {label} : <strong style={{ color: 'var(--fg)' }}>{fmtEur(actual)}</strong>{' '}
      {unavailable ? (
        <span style={{ color: 'var(--dim)' }}>(objectif indisponible : cible inatteignable)</span>
      ) : (
        <span style={{ color }}>
          ({delta >= 0 ? '+' : ''}{fmtEur(delta)} vs objectif)
        </span>
      )}
    </span>
  )
}

/** 'YYYY-MM' → « août 2026 ». */
export function formatMonthLabel(month: string): string {
  const [year, m] = month.split('-')
  const date = new Date(Number(year), Number(m) - 1, 1)
  return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
}

export function MonthEditor({
  month,
  targets,
  settings,
  yearToDateBefore,
  onSave,
  onDelete,
  saving,
}: {
  month: FinanceMonth
  /** Objectifs figés du mois, ou objectifs courants + `frozen: false` (cf. effectiveTargetsFor). */
  targets: EffectiveTargets
  settings: FinanceSettings
  /** Cumul des versements de janvier au mois PRÉCÉDENT (cf. `cumulBeforeMonth`). */
  yearToDateBefore: { salary: number; dividends: number }
  onSave: (draft: MonthDraft) => void
  onDelete: () => void
  saving: boolean
}) {
  const [revenue, setRevenue] = useState(month.revenue)
  const [salaryPaid, setSalaryPaid] = useState(month.salaryPaid)
  const [dividendsPaid, setDividendsPaid] = useState(month.dividendsPaid)
  const [note, setNote] = useState(month.note ?? '')
  // Les lignes RÉELLES d'un mois n'ont pas de périodicité : ce sont des montants
  // décaissés ce mois-là — 'monthly' neutre (le toggle /an n'est pas affiché ici).
  const [lines, setLines] = useState<EditableExpenseLine[]>(
    month.expenses.map((e) => ({ key: e.id, label: e.label, amount: e.amount, active: true, periodicity: 'monthly', dueMonth: null })),
  )
  // recurringId conservé par key pour ne pas perdre le lien à la récurrente d'origine.
  const [recurringByKey] = useState<Map<string, string | null>>(
    () => new Map(month.expenses.map((e) => [e.id, e.recurringId])),
  )

  const expensesTotal = lines.reduce((sum, l) => sum + l.amount, 0)
  // IR DIFFÉRENTIEL sur le cumul annuel : annualiser le mois isolé (×12) surestimait
  // l'impôt dès que les versements sont irréguliers — quatre mois à 3 000 € en début
  // d'activité affichaient de l'IR alors que l'année reste sous la 1re tranche.
  // Le cumul de DIVIDENDES n'entre pas dans le calcul : le PFU est un taux plat,
  // seul l'IR (progressif) a besoin d'un cumul.
  const estimatedNet = estimateNetForMonth(
    {
      salaryYearToDateBefore: yearToDateBefore.salary,
      salaryThisMonth: salaryPaid,
      dividendsThisMonth: dividendsPaid,
    },
    settings,
  )

  const amountInput = (
    value: number,
    onChange: (v: number) => void,
    label: string,
    info?: string,
  ) => (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.75rem', color: 'var(--muted)' }}>
      {/* L'InfoTip ne rend AUCUN texte DOM (aria-label seul) : le textContent du
          libellé reste exactement l'intitulé du champ. */}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
        {label}
        {info === undefined ? null : <InfoTip label={label} text={info} />}
      </span>
      <input
        style={numberField}
        defaultValue={String(value)}
        inputMode="decimal"
        onBlur={(e) => {
          const parsed = parseAmount(e.target.value)
          if (parsed !== null) onChange(parsed)
          else e.target.value = String(value)
        }}
      />
    </label>
  )

  return (
    <section
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: '10px',
        padding: '1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--fg)', margin: 0, textTransform: 'capitalize' }}>
          {formatMonthLabel(month.month)}
        </h3>
        <button
          onClick={onDelete}
          style={{ background: 'transparent', border: 'none', color: 'var(--dim)', fontSize: '0.75rem', fontFamily: 'inherit', cursor: 'pointer' }}
        >
          Supprimer le mois
        </button>
      </div>

      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        {amountInput(revenue, setRevenue, 'CA encaissé (HT)')}
        {amountInput(
          salaryPaid,
          setSalaryPaid,
          'Salaire versé (avant IR)',
          'Le salaire net à payer viré ce mois-ci, AVANT impôt sur le revenu : dans le Calculateur, c’est la ligne « Net avant IR » du canal salaire, pas le « Net salaire » affiché en gras.',
        )}
        {amountInput(
          dividendsPaid,
          setDividendsPaid,
          'Dividendes distribués (avant PFU)',
          'Le montant distribué ce mois-ci, AVANT prélèvement forfaitaire unique : dans le Calculateur, c’est la ligne « Distribuable » du canal dividendes, pas le « Net dividendes » affiché en gras.',
        )}
      </div>

      <ExpenseListEditor
        title="Dépenses réelles"
        lines={lines}
        onChange={setLines}
        showActiveToggle={false}
      />

      <input
        style={{ ...numberField, width: '100%', fontFamily: 'inherit' }}
        defaultValue={note}
        placeholder="Note (optionnel)"
        onBlur={(e) => setNote(e.target.value)}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', borderTop: '1px solid var(--line)', paddingTop: '0.625rem' }}>
        <Delta label="CA" actual={revenue} target={targets.revenue} unavailable={targets.unreachable} />
        <Delta
          label="Dépenses"
          actual={expensesTotal}
          target={targets.expenses}
          invert
          unavailable={targets.unreachable}
        />
        {/* L'objectif de dépenses est MENSUALISÉ (une dépense /an y compte /12) tandis
            que le décaissement tombe en bloc : 1 200 €/an échéant en janvier affiche
            « +1 100 € » en janvier puis « −100 € » onze fois, ce qui n'est pas un
            dérapage puis onze économies. */}
        <span data-testid="expenses-delta-basis" style={{ fontSize: '0.6875rem', color: 'var(--dim)' }}>
          L’objectif de dépenses est lissé : une dépense annuelle y compte pour un douzième,
          alors qu’elle se décaisse en une fois.
        </span>
        {/* L'objectif est un net après impôts : on lui oppose le net estimé (et non le
            brut versé salaire + dividendes, qui gonflerait le delta à tort). Mais les
            deux ne sont comparables qu'EN CUMUL — d'où le ton neutre et la note. */}
        <Delta
          label="Net perso estimé (après impôts)"
          actual={estimatedNet}
          target={targets.net}
          unavailable={targets.unreachable}
          neutral
          testId="delta-net"
        />
        {/* Sans cette note, une année parfaitement conforme affiche quatre mois
            « objectif dépassé » puis cinq mois « objectif manqué » à −527,75 € : l'IR
            est liquidé sur l'ANNÉE, donc les premiers mois n'en portent pas et les
            derniers portent le solde, alors que l'objectif est un net MOYEN. Le
            télescopage est exact sur douze mois — c'est l'échelle du mois qui trompe. */}
        <span data-testid="net-delta-basis" style={{ fontSize: '0.6875rem', color: 'var(--dim)' }}>
          L’IR est liquidé sur l’année entière : les premiers mois n’en portent pas, les derniers
          portent le solde. Ce delta de net ne se juge donc qu’en cumul annuel.
        </span>
        {/* Provenance des objectifs (migration 0012) : figés à la création du mois, ou
            simulation d'aujourd'hui. Le cas figé le dit aussi — sinon un mois figé sous
            un bandeau annonçant un autre objectif n'avait aucune explication de l'écart. */}
        <span data-testid="targets-origin" style={{ fontSize: '0.6875rem', color: 'var(--dim)' }}>
          {targets.frozen
            ? 'Comparé aux objectifs figés à la création de ce mois (pas à la simulation d’aujourd’hui).'
            : 'Comparé aux objectifs courants (ce mois n’a pas d’objectifs figés).'}
        </span>
      </div>

      <button
        onClick={() =>
          onSave({
            revenue,
            salaryPaid,
            dividendsPaid,
            note,
            // Une ligne CHIFFRÉE sans libellé était comptée dans le total affiché puis
            // jetée à l'enregistrement : le total rechargé ne correspondait plus à celui
            // qu'on venait de lire. On l'enregistre donc sous « Sans libellé » — grandeur
            // conservée, libellé rattrapable ensuite. Refuser visiblement bloquerait
            // l'enregistrement de tout le mois pour un simple libellé oublié.
            // Seules les lignes TOTALEMENT vides (ni libellé ni montant) restent jetées :
            // ce sont les lignes ajoutées puis abandonnées, elles ne pèsent rien.
            expenses: lines
              .filter((l) => l.label.trim() !== '' || l.amount > 0)
              .map((l) => ({
                label: l.label.trim() === '' ? 'Sans libellé' : l.label,
                amount: l.amount,
                recurringId: recurringByKey.get(l.key) ?? null,
              })),
          })
        }
        disabled={saving}
        style={{
          alignSelf: 'flex-start',
          background: 'var(--arancio)',
          border: '1px solid var(--arancio-deep)',
          borderRadius: '6px',
          color: '#fff',
          fontSize: '0.8125rem',
          fontWeight: 600,
          fontFamily: 'inherit',
          padding: '0.4375rem 1rem',
          cursor: saving ? 'default' : 'pointer',
          opacity: saving ? 0.6 : 1,
        }}
      >
        {saving ? 'Enregistrement…' : 'Enregistrer'}
      </button>
    </section>
  )
}
